import { useMemo } from "react";
import {
  useQuery as apolloUseQuery,
  useLazyQuery as apolloUseLazyQuery,
  useMutation as apolloUseMutation
} from "@apollo/client/react";
import { transformNullToUndefined } from "./transformNullToUndefined";

/**
 * Custom useQuery hook that transforms null values to undefined
 * This provides TypeScript-friendly undefined values while keeping Apollo Client cache intact
 */
export const useQuery = ((query: any, options: any) => {
  const result = apolloUseQuery(query, options);

  const transformedData = useMemo(
    () => (result.data ? transformNullToUndefined(result.data) : result.data),
    [result.data]
  );

  return {
    ...result,
    data: transformedData
  };
}) as typeof apolloUseQuery;

/**
 * Custom useLazyQuery hook that transforms null values to undefined
 */
export const useLazyQuery = ((query: any, options: any) => {
  const [executeQuery, result] = apolloUseLazyQuery(query, options);

  const transformedData = useMemo(
    () => (result.data ? transformNullToUndefined(result.data) : result.data),
    [result.data]
  );

  const transformedResult = {
    ...result,
    data: transformedData
  };

  return [executeQuery, transformedResult];
}) as typeof apolloUseLazyQuery;

/**
 * Custom useMutation hook that transforms null values to undefined in the result data
 */
export const useMutation = ((mutation: any, options: any) => {
  const [mutate, result] = apolloUseMutation(mutation, options);

  const transformedData = useMemo(
    () => (result.data ? transformNullToUndefined(result.data) : result.data),
    [result.data]
  );

  const transformedResult = {
    ...result,
    data: transformedData
  };

  return [mutate, transformedResult];
}) as typeof apolloUseMutation;
